import { AccordionProps, Stack } from '@mui/material';
import { ReactElement, useMemo } from 'react';
import { ResponsiveFieldGrid } from 'src/components/SidePanelTabs/SiteDocumentation/components/responsive-field-grid';
import { ShowMoreButton } from 'src/components/SidePanelTabs/SiteDocumentation/components/show-more-button';
import { SiteDocumentationAccordion } from 'src/components/SidePanelTabs/SiteDocumentation/components/site-documentation-accordion';
import { renderSubSystemField } from 'src/components/SidePanelTabs/SiteDocumentation/site-documentation.helpers';
import {
  AutoGeneratedFields,
  DocumentationFormField,
  DocumentationFormValue,
  DocumentationFormValues,
} from 'src/components/SidePanelTabs/SiteDocumentation/site-documentation.types';

type CommonFieldProps = Parameters<typeof renderSubSystemField>[0]['props'];

interface SystemInfrastructureSectionProps {
  expanded: boolean;
  onChange: AccordionProps['onChange'];
  fields: DocumentationFormField[];
  shouldShowField: (field: DocumentationFormField) => boolean;
  hasHiddenFields: boolean;
  commonProps: CommonFieldProps;
  onFieldChange: (fieldName: string, value: DocumentationFormValue) => void;
  onResetField: (fieldName: string) => void;
  autoGeneratedFields: AutoGeneratedFields;
  defaultValues: DocumentationFormValues;
  getFormValues: (fieldName: string) => DocumentationFormValue;
  dirtyFields: Record<string, unknown>;
  watchedValues: DocumentationFormValues;
  hasShowMore: boolean;
  showMore: boolean;
  toggleShowMore: () => void;
}

export const SystemInfrastructureSection = ({
  expanded,
  onChange,
  fields,
  shouldShowField,
  hasHiddenFields,
  commonProps,
  onFieldChange,
  onResetField,
  autoGeneratedFields,
  defaultValues,
  getFormValues,
  dirtyFields,
  watchedValues,
  hasShowMore,
  showMore,
  toggleShowMore,
}: SystemInfrastructureSectionProps): ReactElement | null => {
  const renderedFields = useMemo(() => {
    const visibleFields = fields.filter(shouldShowField);
    let fieldsToRender: DocumentationFormField[];

    if (visibleFields.length > 0) {
      fieldsToRender = visibleFields;
    } else if (hasHiddenFields) {
      fieldsToRender = fields.slice(0, 1).map((field) => ({ ...field, alwaysVisible: true }));
    } else {
      fieldsToRender = [];
    }

    return fieldsToRender.map((field: DocumentationFormField) =>
      renderSubSystemField({
        field,
        props: commonProps,
        onFieldChange,
        onResetField,
        autoGeneratedFields,
        defaultValue: defaultValues[field.name],
        getFormValues,
      }),
    );
    // dirtyFields & watchedValues are needed to recalculate when any field is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fields,
    shouldShowField,
    hasHiddenFields,
    commonProps,
    onFieldChange,
    onResetField,
    autoGeneratedFields,
    defaultValues,
    getFormValues,
    dirtyFields,
    watchedValues,
  ]);

  if (!renderedFields.length) {
    return null;
  }

  return (
    <SiteDocumentationAccordion
      title="System Infrastructure"
      expanded={expanded}
      onChange={onChange}
    >
      <Stack gap={1.5}>
        <ResponsiveFieldGrid>{renderedFields}</ResponsiveFieldGrid>
        {hasShowMore && hasHiddenFields ? (
          <ShowMoreButton expanded={showMore} onToggle={toggleShowMore} />
        ) : null}
      </Stack>
    </SiteDocumentationAccordion>
  );
};
